import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiCheck,
  FiPause,
  FiPlay,
  FiChevronUp,
  FiChevronDown,
  FiUser,
  FiUsers,
} from "react-icons/fi";
import { useAuth } from "../../../services/useAuth";
import { withAuth } from "../../../components/HOC/withAuth";
import { useTitle } from "../../../hooks/useTitle";
import AppPage from "../../../components/CustomUI/AppPage";
import CardContainer from "../../../components/CustomUI/CardContainer";
import Loading from "../../../components/CustomUI/Loading";
import SidebarContainer from "../../../components/CustomUI/SidebarContainer";
import { Table, Tr, Td } from "../../../components/BaseUI/Table";
import Button from "../../../components/BaseUI/Button";
import IconButton from "../../../components/BaseUI/Button/IconButton";
import Lozenge from "../../../components/BaseUI/Lozenge";
import Input from "../../../components/BaseUI/Input";
import Label from "../../../components/BaseUI/Label";
import Checkbox from "../../../components/BaseUI/Checkbox";
import ButtonGroup from "../../../components/BaseUI/ButtonGroup";
import { ToastContainer, Toast } from "../../../components/CustomUI/Toast";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
} from "../../../components/BaseUI/Modal";
import { approvalWorkflowsApi } from "../../../api/approvalWorkflows";
import { hasPageWriteAccess } from "../../../config/roles";
import axios from "axios";

const API_URL =
  process.env.REACT_APP_BACKEND_URL ||
  process.env.REACT_APP_API_URL ||
  "http://localhost:3100";

const ENTITY_TYPE_OPTIONS = [
  { value: "order", label: "Teklif" },
  { value: "sale", label: "Sipariş" },
];

const ENTITY_LABEL = Object.fromEntries(
  ENTITY_TYPE_OPTIONS.map((o) => [o.value, o.label]),
);
function ApprovalWorkflows() {
  useTitle("Onay Akış Yönetimi");
  const { getToken, user } = useAuth();
  const canWrite = hasPageWriteAccess(
    user?.role,
    "/approval-workflows",
    user?.pages || [],
    !!user?.fullAccess,
  );

  const [workflows, setWorkflows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    id: null,
    action: null,
  });
  const [toasts, setToasts] = useState([]);

  const navigate = useNavigate();

  const addToast = (message, appearance = "success") => {
    const id = Date.now();
    setToasts((t) => [...t, { id, message, appearance }]);
    setTimeout(() => setToasts((t) => t.filter((x) => x.id !== id)), 4000);
  };

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${getToken()}` },
  });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const wRes = await approvalWorkflowsApi.getAll();
      setWorkflows(wRes.items || []);
    } catch {
      addToast("Veriler yüklenemedi", "danger");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => navigate("/approval-workflows/new");
  const openEdit = (wf) => navigate(`/approval-workflows/edit/${wf._id}`);

  const handleActivate = async (id) => {
    try {
      await approvalWorkflowsApi.activate(id);
      addToast("Onay akışı aktive edildi", "success");
      load();
    } catch (err) {
      addToast(err?.response?.data?.message || "Aktive edilemedi", "danger");
    }
  };

  const handleDeactivate = async (id) => {
    try {
      await approvalWorkflowsApi.deactivate(id);
      addToast("Onay akışı devre dışı bırakıldı", "success");
      load();
    } catch (err) {
      addToast(
        err?.response?.data?.message || "Devre dışı bırakılamadı",
        "danger",
      );
    }
  };

  const handleDelete = async (id) => {
    try {
      await approvalWorkflowsApi.delete(id);
      addToast("Onay akışı silindi", "success");
      load();
    } catch (err) {
      addToast(err?.response?.data?.message || "Silme başarısız", "danger");
    } finally {
      setConfirmModal({ isOpen: false, id: null, action: null });
    }
  };

  const columns = [
    { name: "Akış Adı", dataType: "text", id: 0 },
    { name: "Varlık Türü", dataType: "text", id: 1 },
    { name: "Adım Sayısı", dataType: "text", id: 2 },
    { name: "Durum", dataType: "text", id: 3 },
    { name: "İşlemler", id: 4, sortable: false },
  ];

  return (
    <AppPage
      title="Onay Akış Yönetimi"
      subtitle="Entity türleri için dinamik onay adımlarını ve onaylayıcıları yönetin."
      actions={
        canWrite && (
        <Button
          appearance="primary-animated"
          onClick={openCreate}
          icon={<FiPlus />}
        >
          Yeni Akış
        </Button>
        )
      }
    >
      <CardContainer>
        <Table columns={columns} loading={loading} search>
          {workflows.map((wf) => (
            <Tr key={wf._id} data={wf}>
              <Td>
                <strong style={{ fontSize: "0.875rem" }}>{wf.name}</strong>
                {wf.description && (
                  <div className="text-muted small">{wf.description}</div>
                )}
              </Td>
              <Td>
                <Lozenge appearance="default-subtle">
                  {ENTITY_LABEL[wf.entityType] || wf.entityType}
                </Lozenge>
              </Td>
              <Td>
                <span className="badge bg-secondary-subtle text-secondary-emphasis">
                  {wf.steps?.length || 0} adım
                </span>
              </Td>
              <Td>
                {wf.isActive ? (
                  <Lozenge appearance="success-subtle" isBold>
                    Aktif
                  </Lozenge>
                ) : (
                  <Lozenge appearance="default-subtle" isBold>
                    Pasif
                  </Lozenge>
                )}
              </Td>
              <Td>
                {canWrite ? (
                <div className="d-flex gap-2">
                  {wf.isActive ? (
                    <IconButton
                      icon={<FiPause />}
                      label="Pasifleştir"
                      appearance="subtle"
                      shape="circle"
                      onClick={() => handleDeactivate(wf._id)}
                    />
                  ) : (
                    <IconButton
                      icon={<FiPlay />}
                      label="Aktifleştir"
                      appearance="success"
                      shape="circle"
                      onClick={() => handleActivate(wf._id)}
                    />
                  )}
                  <IconButton
                    icon={<FiEdit2 />}
                    label="Düzenle"
                    appearance="subtle"
                    shape="circle"
                    onClick={() => openEdit(wf)}
                  />
                  <IconButton
                    icon={<FiTrash2 />}
                    label="Sil"
                    appearance="subtle"
                    shape="circle"
                    onClick={() =>
                      setConfirmModal({
                        isOpen: true,
                        id: wf._id,
                        action: "delete",
                      })
                    }
                  />
                </div>
                ) : (
                  <span className="text-muted small">Salt okunur</span>
                )}
              </Td>
            </Tr>
          ))}
        </Table>
      </CardContainer>

      {/* Delete Confirm Modal */}
      <Modal
        isOpen={confirmModal.isOpen}
        onClose={() =>
          setConfirmModal({ isOpen: false, id: null, action: null })
        }
        width="small"
      >
        <ModalHeader>Onay Akışını Sil</ModalHeader>
        <ModalBody>
          <p className="sub-title-page mb-0">
            Bu onay akışını silmek istediğinizden emin misiniz?
          </p>
        </ModalBody>
        <ModalFooter>
          <ButtonGroup placement="end" reverse>
            <Button
              appearance="danger"
              onClick={() => handleDelete(confirmModal.id)}
            >
              Sil
            </Button>
            <Button
              appearance="subtle"
              onClick={() =>
                setConfirmModal({ isOpen: false, id: null, action: null })
              }
            >
              İptal
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </Modal>

      <ToastContainer>
        {toasts.map((t) => (
          <Toast key={t.id} title={t.message} appearance={t.appearance} />
        ))}
      </ToastContainer>
    </AppPage>
  );
}

export default withAuth(ApprovalWorkflows);
